import dayjs from "dayjs"
import { combineDateTime } from "./dateTimeUtils"
import {
  ALL_DAYS,
  type ClinicianSchedule,
  type ClinicianScheduleDay,
} from "./clinicianSchedule"
import type { InitDaysValue } from "./types"

const RECURRING_KEYS: InitDaysValue[] = [
  "recurring_sunday",
  "recurring_monday",
  "recurring_tuesday",
  "recurring_wednesday",
  "recurring_thursday",
  "recurring_friday",
  "recurring_saturday",
]

const DAY_TO_RECURRING: Record<ClinicianScheduleDay, InitDaysValue> = {
  Su: "recurring_sunday",
  M: "recurring_monday",
  Tu: "recurring_tuesday",
  W: "recurring_wednesday",
  Th: "recurring_thursday",
  F: "recurring_friday",
  Sa: "recurring_saturday",
}

export type ScheduleTimeMutationPayload = {
  one_time: boolean
  scheduled_at: string
} & Record<InitDaysValue, boolean>

function timeStringToDayjs(time: string) {
  const match = time.match(/^(\d{1,2}):(\d{2})$/)
  if (!match) {
    return dayjs().hour(10).minute(0).second(0).millisecond(0)
  }
  return dayjs()
    .hour(Number(match[1]))
    .minute(Number(match[2]))
    .second(0)
    .millisecond(0)
}

export function expirationPartsToMinutes(parts: {
  days: string
  hours: string
  minutes: string
}): number {
  const days = Number.parseInt(parts.days, 10) || 0
  const hours = Number.parseInt(parts.hours, 10) || 0
  const minutes = Number.parseInt(parts.minutes, 10) || 0
  return days * 24 * 60 + hours * 60 + minutes
}

function emptyRecurringFlags(): Record<InitDaysValue, boolean> {
  return RECURRING_KEYS.reduce((acc, key) => {
    acc[key] = false
    return acc
  }, {} as Record<InitDaysValue, boolean>)
}

function recurringFlagsFromDays(
  days: ClinicianScheduleDay[] | undefined,
  isRecurring: boolean
): Record<InitDaysValue, boolean> {
  const flags = emptyRecurringFlags()
  if (!isRecurring || !days?.length) {
    return flags
  }
  for (const day of days) {
    flags[DAY_TO_RECURRING[day]] = true
  }
  return flags
}

/**
 * Maps Loop / SetTechniqueModal schedule state to Hasura schedule mutation fields.
 * Mirrors ScheduleDialog save() time + recurrence behavior.
 */
export function buildScheduleTimeMutationPayload(
  schedule: ClinicianSchedule
): ScheduleTimeMutationPayload {
  const isOneTime = schedule.mode === "one_time"
  const selectedTime = timeStringToDayjs(schedule.time)
  const selectedDate = schedule.date ? dayjs(schedule.date) : null

  if (!schedule.time) {
    throw new Error("Please set Notification Time")
  }
  if (isOneTime && !selectedDate) {
    throw new Error("Pick a one time date")
  }
  if (
    isOneTime &&
    selectedDate &&
    selectedDate.isToday() &&
    selectedTime.isBefore(dayjs())
  ) {
    throw new Error("One Time date and time must be in the future")
  }

  const recurringDays = isOneTime
    ? emptyRecurringFlags()
    : recurringFlagsFromDays(schedule.days, true)

  if (!isOneTime && !Object.values(recurringDays).some(Boolean)) {
    throw new Error("Pick at least one recurring day")
  }

  // Hasura nudge_schedules only store one_time + weekday flags — end
  // date/count cannot be persisted on this path.
  const ends = schedule.recurrence?.ends
  if (!isOneTime && ends && ends.type !== "never") {
    throw new Error(
      "Recurring schedules cannot use an end date or occurrence count here"
    )
  }

  let scheduled_at: dayjs.Dayjs
  if (isOneTime && selectedDate) {
    const combined = combineDateTime(selectedDate, selectedTime)
    if (!combined) {
      throw new Error("Pick a one time date")
    }
    scheduled_at = combined
  } else {
    const now = dayjs()
    const selectedWeekdays = ALL_DAYS.map((day, index) => ({
      index,
      selected: schedule.days?.includes(day) ?? false,
    }))
      .filter((d) => d.selected)
      .map((d) => d.index)

    const candidates = selectedWeekdays.map((weekday) => {
      const offsetDays = (weekday - now.day() + 7) % 7
      let candidate = now
        .add(offsetDays, "day")
        .hour(selectedTime.hour())
        .minute(selectedTime.minute())
        .second(0)
        .millisecond(0)

      if (candidate.isBefore(now)) {
        candidate = candidate.add(7, "day")
      }
      return candidate
    })

    if (candidates.length === 0) {
      throw new Error("Pick at least one recurring day")
    }

    scheduled_at = candidates.reduce((earliest, curr) =>
      curr.isBefore(earliest) ? curr : earliest
    )
  }

  return {
    one_time: isOneTime,
    scheduled_at: scheduled_at.format("YYYY-MM-DDTHH:mm"),
    ...recurringDays,
  }
}
